'use client';

import { useCallback, useEffect, useRef } from 'react';
import { taskToast } from '@/lib/task-toast';
import { taskRealtimeClient } from '@/lib/task-realtime/client';
import type { TaskRealtimeEnvelope } from '@/lib/task-realtime/types';
import {
  useLoadTasks,
  useRealtimeConnected,
  useRealtimeError,
  useTaskStore,
} from '@/stores/task-store';

export const useTaskRealtime = () => {
  const loadTasks = useLoadTasks();
  const setRealtimeStatus = useTaskStore((state) => state.setRealtimeStatus);
  const handleRealtimeTaskEvent = useTaskStore((state) => state.handleRealtimeTaskEvent);
  const isConnected = useRealtimeConnected();
  const error = useRealtimeError();
  const hasLoadedInitially = useRef(false);
  const hadVisibleSyncError = useRef(false);

  const reconcile = useCallback((reasons: string[]) => (
    loadTasks({
      silent: true,
      reason: `realtime:${reasons.join(',') || 'event'}`,
    })
  ), [loadTasks]);

  const handleEvent = useCallback((event: TaskRealtimeEnvelope) => {
    if (event.type !== 'TASKS_INVALIDATED') {
      return handleRealtimeTaskEvent(event);
    }
  }, [handleRealtimeTaskEvent]);

  useEffect(() => {
    return taskRealtimeClient.activate({
      onStatusChange: setRealtimeStatus,
      onReconcile: reconcile,
      onEvent: handleEvent,
    });
  }, [handleEvent, reconcile, setRealtimeStatus]);

  useEffect(() => {
    if (hasLoadedInitially.current) {
      return;
    }

    hasLoadedInitially.current = true;
    void loadTasks({ silent: true, reason: 'hook-initial' });
  }, [loadTasks]);

  useEffect(() => {
    if (error) {
      if (!hadVisibleSyncError.current) {
        taskToast.warning('No se pudo sincronizar en tiempo real', {
          description: 'Seguimos mostrando tus tareas mientras se recupera la conexion.',
        });
      }
      hadVisibleSyncError.current = true;
      return;
    }

    if (isConnected && hadVisibleSyncError.current) {
      hadVisibleSyncError.current = false;
      taskToast.success('Sincronizacion recuperada', {
        description: 'La lista de tareas se actualizo desde Camunda.',
      });
    }
  }, [error, isConnected]);

  return { isConnected, error };
};
